import * as Sentry from '@sentry/react'

const KNOWN_NOISE: (string | RegExp)[] = [
  // Browser extension / chromium noise
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  // Firefox internals
  'NS_ERROR_NOT_INITIALIZED',
  // Cancelled navigations / XHR aborts triggered by the user
  /^AbortError/,
  // Dynamic import failures (usually CDN blip, not a code bug)
  /^ChunkLoadError/,
  // Safari back/forward cache restoration noise
  /^Blocked a frame with origin/,
]

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined
  if (!dsn) return

  const isProd = import.meta.env.PROD
  const apiUrl = import.meta.env.VITE_API_URL as string | undefined

  const tracePropagationTargets: (string | RegExp)[] = isProd ? [] : ['localhost']
  if (apiUrl) {
    try {
      tracePropagationTargets.push(new URL(apiUrl).hostname)
    } catch {
      // ignore malformed URL
    }
  }

  Sentry.init({
    dsn,
    environment:
      (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ??
      (isProd ? 'production' : 'development'),
    release: import.meta.env.VITE_APP_VERSION as string | undefined,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Mask all text + block media for privacy compliance.
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Tracing: sample 100% in dev so you see everything; 10% in production.
    tracesSampleRate: Number(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? (isProd ? 0.1 : 1.0)
    ),

    // Session Replay: 10% of normal sessions; always capture on error.
    replaysSessionSampleRate: isProd ? 0.1 : 0,
    replaysOnErrorSampleRate: 1.0,

    // Propagate Sentry trace headers to the Rust backend so events are correlated.
    tracePropagationTargets,

    // Filter known-noisy, non-actionable errors.
    ignoreErrors: KNOWN_NOISE,

    beforeSend(event) {
      // Strip auth tokens from request headers before they reach Sentry servers.
      if (event.request?.headers) {
        delete event.request.headers['Authorization']
        delete event.request.headers['authorization']
        delete event.request.headers['x-api-key']
      }

      // Drop events that are just network-cancelled fetches (status 0 in Axios).
      const extra = event.extra as Record<string, unknown> | undefined
      if (extra?.httpStatus === 0) return null

      return event
    },
  })
}

/** Call after a successful login to attach the user to every future Sentry event. */
export function setSentryUser(user: {
  id: string
  email?: string
  username?: string
  role?: string
}) {
  Sentry.setUser(user)
}

/** Call on logout / session expiry to remove the user from future events. */
export function clearSentryUser() {
  Sentry.setUser(null)
}

export { Sentry }
